import type { HapticPattern } from "./types";
import type { HapticPatternName } from "./HapticPatterns";
import { HapticPatterns } from "./HapticPatterns";
import { VibrationDriver } from "./engine/VibrationDriver";
import type { Log } from "@slot/presentation";

/**
 * The vibration driver behind the `HapticsSettingsStore`: it follows the store's setting and keeps
 * no state of its own between sessions. Preferences do not survive a reload (user, 2026-09-15);
 * every session starts from the defaults.
 */
export interface HapticsService {
  readonly isSupported: boolean;
  readonly enabled: boolean;

  setEnabled(enabled: boolean): void;

  /** Play a named pattern or a custom pattern. */
  play(pattern: HapticPatternName | HapticPattern): void;

  /** Cancel any ongoing vibration. */
  cancel(): void;

  /** Release DOM resources. */
  destroy(): void;
}

export function createHapticsService(params?: {
  log?: Log;
}): HapticsService {
  const log = params?.log;
  const driver = new VibrationDriver();
  driver.initialize();

  let enabled = true;

  return {
    get isSupported() {
      return driver.isSupported;
    },

    get enabled() {
      return enabled;
    },

    setEnabled(value: boolean) {
      enabled = value;
      if (!value) driver.cancel();
      log?.(`[Haptics] setEnabled=${value}`);
    },

    play(pattern: HapticPatternName | HapticPattern) {
      if (!enabled || !driver.isSupported) return;

      const resolved: HapticPattern =
        typeof pattern === "string" ? HapticPatterns[pattern] : pattern;

      driver.play(resolved);
    },

    cancel() {
      driver.cancel();
    },

    destroy() {
      driver.destroy();
    },
  };
}
